import type {
  AlertDetail,
  AlertListItem,
  AlertResolution,
  ApplianceCreated,
  ApplianceEnrollmentResult,
  ApplianceEnrollmentTicket,
  ApplianceView,
  AuthenticatedUser,
  CreateApplianceInput,
  CreateTenantInput,
  EndpointView,
  EnrollApplianceInput,
  FleetOverview,
  KpiDashboard,
  LlmChatResponse,
  LlmMessageView,
  LoginResponse,
  Paginated,
  ReactionPlanView,
  TenantEndpointView,
  TenantView,
} from '@minisoc/contracts';
import { HttpClient, toQueryString } from './http.js';

/** Filtres de la liste d'alertes, tels que manipulés par les fronts. */
export interface AlertsFilter {
  page?: number;
  pageSize?: number;
  severity?: string[];
  status?: string[];
  tenantId?: string;
  ruleCode?: string;
  endpoint?: string;
  search?: string;
  from?: Date;
  to?: Date;
  sort?: 'detectedAt' | 'severity' | 'status' | 'ruleCode';
  order?: 'asc' | 'desc';
}

/**
 * Façade typée de l'API.
 *
 * Regroupe les appels par domaine plutôt que d'exposer `HttpClient` brut : les
 * composants ne construisent jamais de chemin d'URL, ce qui évite de disperser
 * la connaissance du contrat dans les deux applications.
 */
export class MiniSocApi {
  constructor(private readonly http: HttpClient) {}

  // --- Authentification -----------------------------------------------------

  login(email: string, password: string): Promise<LoginResponse> {
    return this.http.anonymous<LoginResponse>('POST', '/auth/login', { email, password });
  }

  logout(refreshToken: string): Promise<void> {
    return this.http.anonymous<void>('POST', '/auth/logout', { refreshToken });
  }

  me(): Promise<AuthenticatedUser> {
    return this.http.get<AuthenticatedUser>('/auth/me');
  }

  // --- Alertes --------------------------------------------------------------

  listAlerts(filter: AlertsFilter = {}): Promise<Paginated<AlertListItem>> {
    return this.http.get<Paginated<AlertListItem>>(`/alerts${toQueryString({ ...filter })}`);
  }

  getAlert(id: string): Promise<AlertDetail> {
    return this.http.get<AlertDetail>(`/alerts/${id}`);
  }

  getReactionPlan(id: string): Promise<ReactionPlanView> {
    return this.http.get<ReactionPlanView>(`/alerts/${id}/reaction-plan`);
  }

  acknowledgeAlert(id: string, note?: string): Promise<AlertDetail> {
    return this.http.patch<AlertDetail>(`/alerts/${id}/acknowledge`, { note });
  }

  closeAlert(
    id: string,
    input: { resolution: AlertResolution; note?: string; completedSteps?: number[] },
  ): Promise<AlertDetail> {
    return this.http.patch<AlertDetail>(`/alerts/${id}/close`, input);
  }

  toggleReactionStep(id: string, order: number, done: boolean): Promise<AlertDetail> {
    return this.http.patch<AlertDetail>(`/alerts/${id}/steps/${order}`, { done });
  }

  reopenAlert(id: string, reason: string): Promise<AlertDetail> {
    return this.http.patch<AlertDetail>(`/alerts/${id}/reopen`, { reason });
  }

  // --- KPI ------------------------------------------------------------------

  getKpiDashboard(days = 30, tenantId?: string): Promise<KpiDashboard> {
    return this.http.get<KpiDashboard>(`/kpi/dashboard${toQueryString({ days, tenantId })}`);
  }

  // --- Assistant IA ---------------------------------------------------------

  chat(alertId: string, message: string, conversationId?: string): Promise<LlmChatResponse> {
    return this.http.post<LlmChatResponse>('/llm/chat', { alertId, message, conversationId });
  }

  getConversation(alertId: string): Promise<LlmMessageView[]> {
    return this.http.get<LlmMessageView[]>(`/llm/conversations/${alertId}`);
  }

  // --- Backoffice : clients -------------------------------------------------

  listTenants(): Promise<TenantView[]> {
    return this.http.get<TenantView[]>('/admin/tenants');
  }

  getFleetOverview(): Promise<FleetOverview> {
    return this.http.get<FleetOverview>('/admin/tenants/fleet-overview');
  }

  getTenant(id: string): Promise<TenantView> {
    return this.http.get<TenantView>(`/admin/tenants/${id}`);
  }

  listTenantEndpoints(id: string): Promise<TenantEndpointView[]> {
    return this.http.get<TenantEndpointView[]>(`/admin/tenants/${id}/endpoints`);
  }

  createTenant(input: CreateTenantInput): Promise<TenantView> {
    return this.http.post<TenantView>('/admin/tenants', input);
  }

  updateTenant(id: string, input: Partial<Omit<CreateTenantInput, 'code'>>): Promise<TenantView> {
    return this.http.patch<TenantView>(`/admin/tenants/${id}`, input);
  }

  suspendTenant(id: string): Promise<TenantView> {
    return this.http.patch<TenantView>(`/admin/tenants/${id}/suspend`);
  }

  // --- Backoffice : nœuds ---------------------------------------------------

  listAppliances(tenantId?: string): Promise<ApplianceView[]> {
    return this.http.get<ApplianceView[]>(`/admin/appliances${toQueryString({ tenantId })}`);
  }

  getAppliance(id: string): Promise<ApplianceView> {
    return this.http.get<ApplianceView>(`/admin/appliances/${id}`);
  }

  listApplianceEndpoints(id: string): Promise<EndpointView[]> {
    return this.http.get<EndpointView[]>(`/admin/appliances/${id}/endpoints`);
  }

  /** Déclare un nœud ; la réponse porte son jeton d'enrôlement, remis une seule fois. */
  createAppliance(input: CreateApplianceInput): Promise<ApplianceCreated> {
    return this.http.post<ApplianceCreated>('/admin/appliances', input);
  }

  issueEnrollmentToken(id: string): Promise<ApplianceEnrollmentTicket> {
    return this.http.post<ApplianceEnrollmentTicket>(`/admin/appliances/${id}/enrollment-token`);
  }

  /** Côté nœud : échange du jeton contre le secret HMAC. Sans authentification. */
  enrollAppliance(input: EnrollApplianceInput): Promise<ApplianceEnrollmentResult> {
    return this.http.anonymous<ApplianceEnrollmentResult>('POST', '/webhooks/enroll', input);
  }

  updateAppliance(
    id: string,
    input: Partial<Omit<CreateApplianceInput, 'tenantId'>>,
  ): Promise<ApplianceView> {
    return this.http.patch<ApplianceView>(`/admin/appliances/${id}`, input);
  }

  rotateWebhookSecret(id: string): Promise<{ webhookSecret: string }> {
    return this.http.post<{ webhookSecret: string }>(
      `/admin/appliances/${id}/rotate-webhook-secret`,
    );
  }

  // --- Backoffice : règles Red Flag ----------------------------------------

  listRules(): Promise<
    {
      id: string;
      code: string;
      name: string;
      nameFr: string;
      severity: string;
      wazuhLevel: number;
      sigmaId: string;
      mitreTactics: string[];
      mitreTechniques: string[];
      isActive: boolean;
    }[]
  > {
    return this.http.get('/admin/rules');
  }

  setRuleActive(code: string, isActive: boolean): Promise<unknown> {
    return this.http.patch(`/admin/rules/${code}/activation`, { isActive });
  }
}
